/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { CheckCircle as CheckIcon, Error as WarningIcon, Info as InfoIcon, Cancel as ErrorIcon } from '@mui/icons-material';

export function InfoBanner( props: {
	buttonRight?: ReactNode;
	children: ReactNode;
	variant: 'success' | 'warning' | 'info' | 'error';
	'data-testid'?: string;
} ): ReactNode {
	const { buttonRight, children, variant } = props;
	const isSuccess = variant === 'success';
	const isInfo = variant === 'info';
	const isError = variant === 'error';
	const BannerIcon = getBannerIcon( isSuccess, isInfo, isError );
	const backgroundColor = getBackgroundColor( isSuccess, isInfo, isError );
	const borderColor = getBorderColor( isSuccess, isInfo, isError );
	const iconColor = getIconColor( isSuccess, isInfo, isError );

	return (
		<Box
			sx={ {
				backgroundColor,
				border: '1px solid',
				borderColor,
				borderRadius: '4px',
				padding: '9px 16px',
				margin: '8px 0'
			} }
			data-testid={ props[ 'data-testid' ] }
		>
			<Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' } }>
				<Box sx={ { display: 'flex', flexFlow: 'row', alignItems: 'center', gap: '10px' } }>
					<BannerIcon
						sx={ {
							width: '16.77px', // This makes the <svg> exactly 14px.
							color: iconColor
						} }
					/>
					<Typography color={ 'text.primary' } fontSize={ '14px' } fontWeight={ 400 } lineHeight={ '22px' }>
						{ children }
					</Typography>
				</Box>
				{ buttonRight }
			</Box>
		</Box>
	);
}

function getBannerIcon( isSuccess: boolean, isInfo: boolean, isError: boolean ) {
	if ( isSuccess ) {
		return CheckIcon;
	}

	if ( isInfo ) {
		return InfoIcon;
	}

	if ( isError ) {
		return ErrorIcon;
	}

	return WarningIcon;
}

function getBackgroundColor( isSuccess: boolean, isInfo: boolean, isError: boolean ) {
	if ( isSuccess ) {
		return 'success.100';
	}

	if ( isInfo ) {
		return 'info.100';
	}

	if ( isError ) {
		return 'error.50';
	}

	return 'warning.100';
}

function getBorderColor( isSuccess: boolean, isInfo: boolean, isError: boolean ) {
	if ( isSuccess ) {
		return 'success.light';
	}

	if ( isInfo ) {
		return 'info.200';
	}

	if ( isError ) {
		return 'error.100';
	}

	return 'warning.200';
}

function getIconColor( isSuccess: boolean, isInfo: boolean, isError: boolean ) {
	if ( isSuccess ) {
		return 'success.400';
	}

	if ( isInfo ) {
		return 'info.400';
	}

	if ( isError ) {
		return 'error.400';
	}

	return 'warning.400';
}
